import { i18n } from "@/main";


export class DateHelper {
  static getTimeDiff(date: string, ignoreTimezone = true): [string, { time: number }] {
    const now = ignoreTimezone ? Date.now() : this.addTimezoneOffset(new Date()).getTime();

    const elapsed = now - Date.parse(date);

    const msPerMinute = 60 * 1000;
    const msPerHour = msPerMinute * 60;
    const msPerDay = msPerHour * 24;
    const msPerMonth = msPerDay * 30;
    const msPerYear = msPerDay * 365;

    let txt = "";
    let time = 0;

    if (elapsed < msPerMinute) {
      txt = "seconds-ago";
      time = Math.round(elapsed / 1000);
    } else if (elapsed < msPerHour) {
      txt = "minutes-ago";
      time = Math.round(elapsed / msPerMinute);
    } else if (elapsed < msPerDay) {
      txt = "hours-ago";
      time = Math.round(elapsed / msPerHour);
    } else if (elapsed < msPerMonth) {
      txt = "days-ago";
      time = Math.round(elapsed / msPerDay);
    } else if (elapsed < msPerYear) {
      txt = "months-ago";
      time = Math.round(elapsed / msPerMonth);
    } else {
      txt = "years-ago";
      time = Math.round(elapsed / msPerYear);
    }

    return [txt, { time }];
  }

  static subtractTimezoneOffset(d: Date): Date {
    return new Date(d.getTime() - (d.getTimezoneOffset() * 60 * 1000));
  }

  static addTimezoneOffset(d: Date): Date {
    return new Date(d.getTime() + (d.getTimezoneOffset() * 60 * 1000));
  }

  static getDate(date: string | number | Date, ignoreTimezone = false): Date {
    let convertedDate: Date | null = null;

    if (typeof date === "string") {
      convertedDate = new Date(Date.parse(date));
    } else if (typeof date === "number") {
      convertedDate =  new Date(date);
    } else {
      convertedDate = date;
    }

    if (convertedDate && ignoreTimezone) {
      return this.subtractTimezoneOffset(convertedDate);
    }

    return convertedDate;
  }

  static toDate(date: string | number | Date, ignoreTimezone = false): string {
    return this.getDate(date, ignoreTimezone).toISOString().substring(0, 10);
  }

  /** Returns a locale-aware, human-readable date string (e.g. "June 15, 2024"). */
  static toLocaleDateString(date: string | number | Date, ignoreTimezone = false): string {
    const d = this.getDate(date, ignoreTimezone);
    const locale = (i18n.locale || "en") as string;
    return d.toLocaleDateString(locale, { dateStyle: "long" });
  }

  static toTime(date: string | number | Date, ignoreTimezone = false): string {
    return this.getDate(date, ignoreTimezone).toISOString().substring(11, 16);
  }
  static toTimeWithSeconds(date: string | number | Date, ignoreTimezone = false): string {
    return this.getDate(date, ignoreTimezone).toISOString().substring(11, 19);
  }
  static toDateTime(date: string | number | Date, ignoreTimezone = false): string {
    return this.toDate(date, ignoreTimezone) + " " + this.toTime(date, ignoreTimezone);
  }

  /** Compact UTC range: same day keeps the date once ("2026-03-17 09:12 – 11:48 UTC"). */
  static toDateTimeRange(start: string, end: string): string {
    const startDate = this.toDate(start, true);
    const endDate = this.toDate(end, true);
    const startTime = this.toTime(start, true);
    const endTime = this.toTime(end, true);

    if (startDate === endDate && startTime === endTime) {
      return `${startDate} ${startTime} UTC`;
    }

    if (startDate === endDate) {
      return `${startDate} ${startTime} – ${endTime} UTC`;
    }

    return `${startDate} ${startTime} – ${endDate} ${endTime} UTC`;
  }
  static toDateTimeWithSeconds(date: string | number | Date, ignoreTimezone = false): string {
    return this.toDate(date, ignoreTimezone) + " " + this.toTimeWithSeconds(date, ignoreTimezone);
  }

  /**
   * Renders an absolute UTC instant (ISO string from FH2) in a specific IANA time zone.
   * Uses 24-hour clock; date format matches `toDateTimeWithSeconds` (YYYY-MM-DD HH:MM:SS).
   */
  static toDateTimeWithSecondsInZone(isoUtc: string, ianaTimeZone: string): string {
    const ms = Date.parse(isoUtc);
    if (Number.isNaN(ms)) {
      return String(isoUtc);
    }
    const d = new Date(ms);
    return d.toLocaleString("sv-SE", {
      timeZone: ianaTimeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  }

  /** Wall-clock datetime plus UTC offset label, e.g. `2026-05-18 13:13:13 (UTC+02)`. */
  static toDateTimeWithSecondsAndUtcOffset(
    isoUtc: string,
    ianaTimeZone?: string | null,
    ignoreTimezoneWhenNoZone = true,
  ): string {
    const formatted = ianaTimeZone
      ? this.toDateTimeWithSecondsInZone(isoUtc, ianaTimeZone)
      : this.toDateTimeWithSeconds(isoUtc, ignoreTimezoneWhenNoZone);
    const offsetLabel = this.formatUtcOffsetLabel(isoUtc, ianaTimeZone);
    return offsetLabel ? `${formatted} ${offsetLabel}` : formatted;
  }

  /** Offset for the instant in `ianaTimeZone`, or `(UTC+00)` when no zone is given. */
  static formatUtcOffsetLabel(isoUtc: string, ianaTimeZone?: string | null): string {
    const ms = Date.parse(isoUtc);
    if (Number.isNaN(ms)) {
      return "";
    }
    const offsetMinutes = ianaTimeZone
      ? this.getTimeZoneOffsetMinutes(new Date(ms), ianaTimeZone)
      : 0;
    return this.formatUtcOffsetFromMinutes(offsetMinutes);
  }

  private static getTimeZoneOffsetMinutes(date: Date, ianaTimeZone: string): number {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: ianaTimeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(date);
    const part = (type: Intl.DateTimeFormatPartTypes): number =>
      Number(parts.find((p) => p.type === type)?.value ?? 0);
    const zonedAsUtc = Date.UTC(
      part("year"),
      part("month") - 1,
      part("day"),
      part("hour"),
      part("minute"),
      part("second"),
    );
    return Math.round((zonedAsUtc - date.getTime()) / 60000);
  }

  private static formatUtcOffsetFromMinutes(offsetMinutes: number): string {
    const sign = offsetMinutes >= 0 ? "+" : "-";
    const abs = Math.abs(offsetMinutes);
    const hours = Math.floor(abs / 60);
    const minutes = abs % 60;
    const hh = String(hours).padStart(2, "0");
    if (minutes === 0) {
      return `(UTC${sign}${hh})`;
    }
    const mm = String(minutes).padStart(2, "0");
    return `(UTC${sign}${hh}:${mm})`;
  }
  static now(): string {
    return this.subtractTimezoneOffset(new Date()).toISOString();
  }

  static toStartOfDay(date: string | number | Date, ignoreTimezone = false): string {
    return this.toDate(date, ignoreTimezone) + " 00:00:00";
  }
  static toEndOfDay(date: string | number | Date, ignoreTimezone = false): string {
    return this.toDate(date, ignoreTimezone) + " 23:59:59";
  }

  static getWeekDay(date: string | number | Date, ignoreTimezone = false): string {
    const d = this.getDate(date, ignoreTimezone);
    const days = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    
    return i18n.t(days[d.getDay()]).toString();
  }

  /** Monday-first short weekday labels for the current locale (e.g. ["Mo", "Di", …]). */
  static localizedDowLabels(): string[] {
    const locale = (i18n.locale || "en") as string;
    return [1, 2, 3, 4, 5, 6, 0].map(d =>
      new Date(2024, 0, d + 1).toLocaleDateString(locale, { weekday: "short" })
    );
  }

  /** Formats a YYYY-MM-DD date string as a short human-readable label (e.g. "15 Jun"). */
  static toShortDateLabel(dateStr: string): string {
    const locale = (i18n.locale || "en") as string;
    const d = this.getDate(dateStr, true);
    return d.toLocaleDateString(locale, { day: "2-digit", month: "short" });
  }

  /** Converts a year, month (0-based) and day number to a YYYY-MM-DD string. */
  static toDayDateStr(year: number, month: number, day: number): string {
    const m = String(month + 1).padStart(2, "0");
    const d = String(day).padStart(2, "0");
    return `${year}-${m}-${d}`;
  }

  /** Local timestamp for download filenames, e.g. `20260528_165244`. */
  static toDownloadFilenameTimestamp(date: Date = new Date()): string {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}_${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
  }

  static getWeekNumber(date: string | number | Date, ignoreTimezone = false): number {
    const d = this.getDate(date, ignoreTimezone);

    // copied from stackoverflow: https://stackoverflow.com/a/6117889
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil((((d.getDate() - yearStart.getDate()) / 86400000) + 1) / 7);

    return weekNo;
  }
}
